import React from 'react';
import {GridColDef, GridRenderCellParams, GridValueGetter} from '@mui/x-data-grid-pro';
import {Delete, OpenInNewOutlined} from '@mui/icons-material';
import {Button} from '@mui/material';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {OVBuilders_ovrecommends} from '../../../schema-types/OVBuilders';
import {OVBuilderRecommendation} from '../../../schema-types/OVBuilderRecommendation';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {themeColors} from '../../../theme/Theme';
import {BUILDER_TYPES, serverToBuilderTypeLabel} from '../constants/SolutionType';
import {URGENCY} from '../../../constants/Urgency';
import {OpenInCuro} from '../../../utils/getCuroURL';
import {RenderColor} from '../../../components/EditorGrid/GridRenderComponents';

import {useAccountContext} from '../../../contexts/AccountContext';
import {
  deleteSelectedBuilderFactory,
  onCompletedDeleteBuilderFactory,
  updateDeleteRecommendation,
} from './BuilderGridHelpers';
import {DELETE_OV_BUILDER, OV_BUILDER_ASSOCIATED_ITEMS} from './builderOvPotQueries';
import {
  OvbuilderAssociatedItems,
  OvbuilderAssociatedItemsVariables,
} from '../../../schema-types/OvbuilderAssociatedItems';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {
  DeleteOvbuilder,
  DeleteOvbuilderVariables,
} from '../../../schema-types/DeleteOvbuilder';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {useReviewOVBuilds as useReviewOVBuilders} from '../hooks/useOVBuilds';
import {renderCellDateTime} from '../../../utils/renderCellDateTime';

export const DRAFTING_ADVICE_STATUS = 'Drafting advice';
export const ADVICE_ACCEPTED_STATUS = 'Advice accepted';

export const getBuilderStatus = (recc: OVBuilderRecommendation) => {
  if (recc.recommends?.some((x) => x.isFailedSubmission)) {
    return 'Error';
  }
  return recc.recommends && recc.recommends.length > 0 && recc.recommends[0]?.curoId
    ? 'Submitted'
    : 'Draft';
};

type RecommendationSubmissionState = {
  recommends?: {curoId?: unknown; recommendationStatus?: string | null}[] | null;
} | null;

export const isAwaitingSubmission = (recc?: RecommendationSubmissionState) =>
  recc?.recommends?.[0]?.recommendationStatus === DRAFTING_ADVICE_STATUS;

export const isSubmittedToCuro = (recc?: RecommendationSubmissionState) =>
  !!recc?.recommends?.some((x) => !!x.curoId) && !isAwaitingSubmission(recc);

export const canDeleteBuilder = (recc: OVBuilderRecommendation) =>
  getBuilderStatus(recc) !== 'Submitted' || isAwaitingSubmission(recc);

export const valueGetterStatus: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  return getBuilderStatus(row);
};

export const valueGetterFinalised: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  return row.isFinalised ? 'Finalised' : 'Not finalised';
};

export const valueGetterAdvice: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  const label = serverToBuilderTypeLabel(row.solutionType);
  return label;
};

export const valueGetterName: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  return row.name || 'No name given';
};

export const valueGetterGoal: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  return row.goal?.name;
};

export const valueGetterUrgency: GridValueGetter<OVBuilderRecommendation> = (
  params,
  row
) => {
  return row.urgency;
};

/**
 * Every mandate selected on a builder recommendation, in pot order.
 *
 * A dentist recommendation can carry more than one investment strategy (MIS):
 * `Set Up New` creates a destination OVRecommendPot per subplan — each holding
 * its own mandate — alongside a wrapping parent pot that holds none. A mandate
 * is either a Saltus one (`modelPortfolio`) or a non-Saltus one (`mandate`),
 * and for `Contribute` / `Transfer` into an existing subplan it lives on the
 * underlying pot rather than on the OVRecommendPot.
 *
 * `Modify` is the exception: it has no destination pot and records the newly
 * chosen mandate on its single source OVRecommendPot, so the underlying pot is
 * deliberately not consulted there — that would surface the mandate being
 * replaced (or an unrelated one when the modification isn't a mandate change).
 */
export const getSelectedMandates = (
  row: Pick<OVBuilders_ovrecommends, 'action' | 'potPlans'>
): string[] => {
  const isModify = row.action === OVRecommendActionEnum.MODIFY;
  const planType = isModify ? 'source' : 'destination';

  return (row.potPlans ?? [])
    .filter((ovrPot) => ovrPot.planType === planType)
    .map((ovrPot) => {
      const onOvrPot = ovrPot.modelPortfolio?.name || ovrPot.mandate;
      if (isModify) {
        return onOvrPot;
      }
      return onOvrPot || ovrPot.pot.modelPortfolio?.name || ovrPot.pot.mandate;
    })
    .filter((mandate): mandate is string => Boolean(mandate));
};

export const valueGetterMandate: GridValueGetter<OVBuilders_ovrecommends> = (
  params,
  row
) => {
  return getSelectedMandates(row).join(', ');
};

export function builderGridRowActions(
  params: GridRenderCellParams<OVBuilderRecommendation>
) {
  const {row} = params;
  return (
    <>
      {canDeleteBuilder(row) ? <BuilderActions builderId={row.id as string} /> : null}
      <Button
        className="open-recc"
        variant="text"
        onClick={() => {
          const path = row.isDentist ? './builder' : './recommends-builder';
          window.open(`${path}?ovrecommendId=${row.id}`, '_blank');
        }}
      >
        <OpenInNewOutlined />
      </Button>
      <ShowIfDebugWrapper>
        {row.recommends?.length
          ? row.recommends.map((r, index) => {
              return (
                <OpenInCuro key={r.curoId} curoId={r.curoId} curoType="recommendation">
                  {index}
                </OpenInCuro>
              );
            })
          : null}
      </ShowIfDebugWrapper>
    </>
  );
}

type BuilderColumnType = GridColDef<OVBuilders_ovrecommends> & {
  field: keyof OVBuilders_ovrecommends;
};

export const ehonsBuilderColumns: BuilderColumnType[] = [
  {
    field: 'id',
    headerName: 'Status',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Submitted: themeColors.lightGreen,
      Draft: themeColors.greyLight,
      Failed: themeColors.red,
    }),
    valueGetter: valueGetterStatus,
  },
  {
    field: 'isFinalised',
    headerName: 'Finalised',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Finalised: themeColors.lightGreen,
      'Not finalised': themeColors.greyLight,
    }),
    valueGetter: valueGetterFinalised,
  },
  {
    field: 'solutionType',
    headerName: 'Advice',
    flex: 1,
    headerAlign: 'center',

    renderCell: RenderColor({
      [BUILDER_TYPES.Recommendation]: themeColors.lightYellow,
      [BUILDER_TYPES.Instruction]: themeColors.lightBlue,
    }),
    valueGetter: valueGetterAdvice,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    headerAlign: 'center',

    valueGetter: valueGetterName,
  },
  {
    field: 'selectedMandate',
    headerName: 'Mandate',
    flex: 2,
    headerAlign: 'center',
    // Derived from the recommendation's pots rather than read from
    // `selectedMandate`, which only ever reports a single mandate and misses
    // MIS groups entirely (DBO-1433).
    valueGetter: valueGetterMandate,
  },
  // {
  //   field: 'goal',
  //   headerName: 'Linked Goal',
  //   flex: 2,
  //   headerAlign: 'center',

  //   valueGetter: valueGetterGoal,
  // },
  {
    field: 'urgency',
    headerName: 'Urgency',
    flex: 1,
    headerAlign: 'center',

    valueGetter: valueGetterUrgency,
    renderCell: RenderColor({
      [URGENCY.NORMAL_ID]: themeColors.greyLight,
      [URGENCY.TAX_ID]: themeColors.lightGreen,
      [URGENCY.URGENT_ID]: themeColors.lightAlert,
      default: themeColors.greyLight,
    }),
  },
  {
    field: 'dateCreated',
    headerName: 'Date Created',
    flex: 1,
    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row?.dateCreated?.valueOf(); // used for ordering
    },
  },
  {
    field: 'dateUpdated',
    headerName: 'Last Updated',
    flex: 1,

    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row.dateUpdated?.valueOf(); // used for ordering
    },
  },
  {
    field: '__typename',
    headerName: ' ',
    flex: 1,
    headerAlign: 'center',

    renderCell: builderGridRowActions,
  },
];

export const BuilderActions: React.FC<{builderId: string}> = ({builderId}) => {
  const {accountId} = useAccountContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {reviewId, refreshReview} = useReviewContext();
  const {data} = useReviewOVBuilders();
  const [getAssociatedReccs, {data: associatedReccsData, error: associatedReccsError}] =
    useLazyQuery<OvbuilderAssociatedItems, OvbuilderAssociatedItemsVariables>(
      OV_BUILDER_ASSOCIATED_ITEMS
    );
  const onCompletedDeleteRecommendation =
    onCompletedDeleteBuilderFactory(enqueueSnackbar);
  const [deleteRecommendation] = useMutation<DeleteOvbuilder, DeleteOvbuilderVariables>(
    DELETE_OV_BUILDER,
    {
      onCompleted: (deletionData) => {
        onCompletedDeleteRecommendation(deletionData);
        // Do not delete the function call below. It makes sure the tubestop is up-to-date (DBO-1432).
        refreshReview();
      },
      onError: (err) => {
        enqueueSnackbar(`Unable to delete the recommendation. ${err.message}`, {
          variant: 'error',
          autoHideDuration: null,
        });
      },
      update: updateDeleteRecommendation,
    }
  );
  const recommends =
    data?.ovrecommends?.filter((r) => {
      if (!r?.review?.id) {
        return false;
      }
      const inThisReview = r.review.id === reviewId;
      return inThisReview;
    }) || [];

  const deleteSelectedBuilder = deleteSelectedBuilderFactory({
    recommends,
    enqueueSnackbar,
    getAssociatedReccs,
    closeSnackbar,
    accountId,
    reviewId,
    deleteRecommendation,
  });
  return (
    <Button
      data-test="Delete recc"
      variant="text"
      onClick={() => deleteSelectedBuilder(builderId)}
    >
      <Delete />
    </Button>
  );
};
